/**
 * Fleet Invoice Slice
 *
 * State for the fleet portal's invoices page (CLIENT users):
 * - Paginated list of the fleet customer's own invoices, newest first
 * - PDF download (issued invoices only — drafts have no PDF)
 *
 * Everything is scoped server-side by the customerUuid JWT claim.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    listFleetInvoicesApi,
    downloadFleetInvoicePdfApi,
} from '../../services/fleetInvoiceMgtApi.js';

// ============================================
// Async Thunks
// ============================================

export const fetchFleetInvoices = createAsyncThunk(
    'fleetInvoices/fetchFleetInvoices',
    // `from` / `to` are optional inclusive ISO dates; passed straight through so the
    // caller owns the window and the slice does not have to know about the phone
    // app's 7-day default.
    async ({ page = 0, size = 20, from, to } = {}, { rejectWithValue }) => {
        try {
            const data = await listFleetInvoicesApi({ page, size, from, to });
            return { data, page, size };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load your invoices.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch the PDF and hand the browser a download.
 *
 * The blob is turned into an object URL and clicked, then revoked — the PDF
 * itself is never put in Redux state (a Blob isn't serialisable, and there's no
 * reason to keep it around).
 */
export const downloadFleetInvoicePdf = createAsyncThunk(
    'fleetInvoices/downloadFleetInvoicePdf',
    async ({ uuid, invoiceNumber }, { rejectWithValue }) => {
        try {
            const blob = await downloadFleetInvoicePdfApi(uuid);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${invoiceNumber || 'invoice'}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return uuid;
        } catch (error) {
            // The error body is a Blob when responseType is 'blob', so the usual
            // error.response.data.message isn't readable — keep it simple.
            const message = error.response?.status === 404
                ? 'That invoice could not be found.'
                : 'Failed to download the invoice.';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    items: [],
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    loading: false,
    error: null,

    // uuid of the invoice currently downloading
    downloadingUuid: null,
    downloadError: null,
};

const fleetInvoiceSlice = createSlice({
    name: 'fleetInvoices',
    initialState,
    reducers: {
        clearFleetInvoiceErrors(state) {
            state.error = null;
            state.downloadError = null;
        },
    },
    extraReducers: (builder) => {
        // ---- List ----
        builder
            .addCase(fetchFleetInvoices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFleetInvoices.fulfilled, (state, action) => {
                const { data, page, size } = action.payload;
                state.loading = false;
                state.items = data.content || [];
                state.page = data.page ?? page;
                state.size = data.size ?? size;
                state.totalElements = data.totalElements ?? 0;
                state.totalPages = data.totalPages ?? 0;
            })
            .addCase(fetchFleetInvoices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load your invoices.';
            });

        // ---- PDF download ----
        builder
            .addCase(downloadFleetInvoicePdf.pending, (state, action) => {
                state.downloadingUuid = action.meta.arg.uuid;
                state.downloadError = null;
            })
            .addCase(downloadFleetInvoicePdf.fulfilled, (state) => {
                state.downloadingUuid = null;
            })
            .addCase(downloadFleetInvoicePdf.rejected, (state, action) => {
                state.downloadingUuid = null;
                state.downloadError = action.payload || 'Failed to download the invoice.';
            });
    },
});

export const { clearFleetInvoiceErrors } = fleetInvoiceSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectFleetInvoices = (state) => state.fleetInvoices.items;
export const selectFleetInvoicesLoading = (state) => state.fleetInvoices.loading;
export const selectFleetInvoicesError = (state) => state.fleetInvoices.error;
export const selectFleetInvoicesPage = (state) => state.fleetInvoices.page;
export const selectFleetInvoicesTotalPages = (state) => state.fleetInvoices.totalPages;
export const selectFleetInvoicesTotalElements = (state) => state.fleetInvoices.totalElements;
export const selectFleetInvoiceDownloadingUuid = (state) => state.fleetInvoices.downloadingUuid;
export const selectFleetInvoiceDownloadError = (state) => state.fleetInvoices.downloadError;

export default fleetInvoiceSlice.reducer;